<template>
  <div data-page-name="legal">
    <div class="wrapper">
      <h1>{{ $t('Legal') }}</h1>

      <ul class="list">
        <li
          v-for="{ id, slug, data: { title } } of previewsByLocale"
          :key="id"
          class="item"
        >
          <ButtonLinkUnderlined :to="createLink(slug)">
            {{
              truncate(title, {
                length: 60,
                separator: /,? +/,
                omission: '...',
              })
            }}
          </ButtonLinkUnderlined>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { showError } from '#app';

// components
import ButtonLinkUnderlined from '~/components/redesign/ButtonLinkUnderlined.vue';

// composables
import { useI18n } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { PATHS } from '~/constants/nav-items';
import { PostType } from '~/types';

// utils
import get from 'lodash.get';
import truncate from 'lodash.truncate';

// types
import type { BlogArticle } from '~/types/blog';

const { locale } = useI18n();
const localePathWithSlash = useLocalePathWithSlash();

const { error, data: articles } = useAsyncGql({
  operation: 'BlogArticlesPreviews',
  variables: {
    first: 25,
    types: PostType.LEGAL,
  },
  options: {
    transform: data => data.blog_articles.data,
  },
});

if (error.value) {
  showError(
    `Failed to fetch data. Operation: ${error.value?.operationName}; StatusCode: ${error.value?.statusCode}`,
  );
}

const previewsByLocaleVisibility = computed(() => {
  const previews: Record<string, BlogArticle[]> = {};

  if (articles.value) {
    articles.value.reduce((acc, preview) => {
      const visible_for = get(preview, 'data.data.visible_for', []) as string[];

      visible_for.forEach(language => {
        if (!acc[language]) {
          acc[language] = [];
        }

        acc[language].push(preview);
      });

      return acc;
    }, previews);
  }

  return previews;
});

const previewsByLocale = computed(() => {
  return previewsByLocaleVisibility.value[locale.value]?.length
    ? previewsByLocaleVisibility.value[locale.value]
    : previewsByLocaleVisibility.value['en'];
});

const createLink = (slug: string) => {
  return localePathWithSlash(PATHS.LEGAL.path + slug);
};

useApiSeo('legal');
useHead({ meta: [{ name: 'robots', content: 'noindex' }] });
</script>

<style scoped lang="scss">
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/mixins/typo';

[data-page-name='legal'] {
  .wrapper {
    padding: 5rem 2rem 8rem;

    @include size.wrapper(80rem);

    @include media.mobile {
      padding: 4rem 1rem 5rem;
    }

    h1 {
      @include typo.h2;

      margin-bottom: 4rem;

      @include media.tablet {
        margin-bottom: 2.5rem;
      }

      @include media.mobile {
        margin-bottom: 2rem;
      }
    }

    ul.list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(4, auto);
      grid-auto-flow: column;
      grid-gap: 0 2rem;

      @include media.tablet {
        grid-template-columns: repeat(1, 1fr);
        grid-auto-flow: row;
      }

      li.item {
        margin-top: 2rem;

        @include media.tablet {
          margin-top: 1.5rem;
        }

        @include media.mobile {
          margin-top: 1rem;
        }

        &:nth-child(1),
        &:nth-child(5) {
          margin-top: 0;
        }
      }
    }
  }
}
</style>
